import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /** @type {HTMLAnchorElement | null} */
  #legacyTouchLink = null;

  /** @type {number | null} */
  #legacyTouchId = null;

  #legacyTouchStartX = 0;
  #legacyTouchStartY = 0;
  #usesLegacyIOSTouchRouting = false;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();

    this.#usesLegacyIOSTouchRouting = this.#isLegacyIOSWebKit();
    if (this.#usesLegacyIOSTouchRouting) {
      // `overflow: hidden` on the root element can desynchronize Safari 15's
      // visual viewport and fixed-element hit testing. The drawer already
      // covers the viewport and owns its scrolling, so skip that lock here.
      this.refs.details.removeAttribute('scroll-lock');

      this.refs.menuDrawer.addEventListener('touchstart', this.#onLegacyTouchStart, {
        passive: true,
        capture: true,
      });
      this.refs.menuDrawer.addEventListener('touchend', this.#onLegacyTouchEnd, {
        passive: false,
        capture: true,
      });
      this.refs.menuDrawer.addEventListener('touchcancel', this.#onLegacyTouchCancel, {
        passive: true,
        capture: true,
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);

    if (this.#usesLegacyIOSTouchRouting) {
      this.refs.menuDrawer.removeEventListener('touchstart', this.#onLegacyTouchStart, true);
      this.refs.menuDrawer.removeEventListener('touchend', this.#onLegacyTouchEnd, true);
      this.refs.menuDrawer.removeEventListener('touchcancel', this.#onLegacyTouchCancel, true);
    }
    this.#resetLegacyTouch();
  }

  #isLegacyIOSWebKit() {
    const isAppleWebKit = /AppleWebKit/i.test(navigator.userAgent);
    const isIOS =
      /iPad|iPhone|iPod/i.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const lacksContainerQueries = typeof CSS === 'undefined' || !CSS.supports?.('container-type', 'inline-size');

    return isAppleWebKit && isIOS && lacksContainerQueries;
  }

  #findTouch(touchList) {
    if (this.#legacyTouchId === null) return null;
    for (let index = 0; index < touchList.length; index++) {
      if (touchList[index].identifier === this.#legacyTouchId) return touchList[index];
    }
    return null;
  }

  #linkAtTouchPoint(touch, eventTarget) {
    // Safari reports touch coordinates from the visual viewport while fixed
    // drawer rectangles can remain relative to the layout viewport. The
    // difference is roughly one menu row in the affected Instagram WebView.
    // Add the visual viewport offset so both values share one coordinate space.
    const viewportOffsetX = window.visualViewport?.offsetLeft || 0;
    const viewportOffsetY = window.visualViewport?.offsetTop || 0;
    const x = touch.clientX + viewportOffsetX;
    const y = touch.clientY + viewportOffsetY;
    const links = this.refs.menuDrawer.querySelectorAll('a.menu-drawer__menu-item[href]');

    for (const link of links) {
      if (!(link instanceof HTMLAnchorElement)) continue;
      const rect = link.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) continue;
      if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) return link;
    }

    if (!(eventTarget instanceof Element)) return null;
    const targetLink = eventTarget.closest('a.menu-drawer__menu-item[href]');
    return targetLink instanceof HTMLAnchorElement && this.refs.menuDrawer.contains(targetLink) ? targetLink : null;
  }

  #onLegacyTouchStart = (event) => {
    this.#resetLegacyTouch();
    if (event.touches.length !== 1) return;

    const touch = event.changedTouches[0] || event.touches[0];
    if (!touch) return;

    const link = this.#linkAtTouchPoint(touch, event.target);
    if (!link) return;

    this.#legacyTouchLink = link;
    this.#legacyTouchId = touch.identifier;
    this.#legacyTouchStartX = touch.pageX;
    this.#legacyTouchStartY = touch.pageY;
  };

  #onLegacyTouchEnd = (event) => {
    const touch = this.#findTouch(event.changedTouches);
    const link = this.#legacyTouchLink;
    if (!touch || !link) {
      this.#resetLegacyTouch();
      return;
    }

    const moved =
      Math.abs(touch.pageX - this.#legacyTouchStartX) > 12 ||
      Math.abs(touch.pageY - this.#legacyTouchStartY) > 12;
    this.#resetLegacyTouch();

    // A drag belongs to the drawer's native vertical scrolling.
    if (moved) return;

    // Suppress WebKit's offset synthetic click, then activate the exact link
    // whose visible rectangle received touchstart.
    if (event.cancelable) event.preventDefault();
    event.stopPropagation();
    if (link.isConnected) link.click();
  };

  #onLegacyTouchCancel = () => {
    this.#resetLegacyTouch();
  };

  #resetLegacyTouch() {
    this.#legacyTouchLink = null;
    this.#legacyTouchId = null;
    this.#legacyTouchStartX = 0;
    this.#legacyTouchStartY = 0;
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
